import { useState, useEffect, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  Code2,
  FileCode,
  Server,
  Cloud,
  Palette,
  Database,
  Settings,
  Lightbulb,
  Loader2,
  RefreshCw,
  FolderOpen,
  Trash2,
  type LucideIcon
} from 'lucide-react'
import { AISkillAnalyzer } from '@/components/ai'
import { cn } from '@/lib/utils'
import { suggestNextSkills, type AnalyzedSkill, type SkillSuggestion } from '@/services/ai'
import { syncProjectsToSkills, onProjectsUpdated, saveSkillsToStorage, loadSkillsFromStorage } from '@/services/projectSkillSync'

const SUGGESTIONS_STORAGE_KEY = 'portfolio-skill-suggestions'
const SKILLS_STORAGE_KEY = 'portfolio-skills'

interface DisplaySkill {
  name: string
  level: number
  category: 'Frontend' | 'Backend' | 'Language' | 'Infrastructure' | 'Other'
  icon: LucideIcon
}

const iconMap: Record<string, LucideIcon> = {
  'React': Code2,
  'Vue': Code2,
  'Angular': Code2,
  'TypeScript': FileCode,
  'JavaScript': FileCode,
  'Python': FileCode,
  'Java': FileCode,
  'Go': FileCode,
  'Rust': FileCode,
  'Node.js': Server,
  'Express': Server,
  'Django': Server,
  'FastAPI': Server,
  'PostgreSQL': Database,
  'MySQL': Database,
  'MongoDB': Database,
  'Redis': Database,
  'AWS': Cloud,
  'GCP': Cloud,
  'Azure': Cloud,
  'Cloudflare': Cloud,
  'Docker': Server,
  'Kubernetes': Server,
  'Terraform': Settings,
  'Tailwind CSS': Palette,
  'CSS': Palette,
  'HTML': Code2,
  'GitHub Actions': Settings,
  'Git': Settings,
}

const categories = ['All', 'Frontend', 'Backend', 'Language', 'Infrastructure', 'Other'] as const
type Category = typeof categories[number]

function getIconForSkill(name: string): LucideIcon {
  return iconMap[name] || Settings
}

function convertToDisplaySkills(skills: AnalyzedSkill[]): DisplaySkill[] {
  return skills.map(skill => ({
    ...skill,
    icon: getIconForSkill(skill.name)
  }))
}

export function Skills() {
  const navigate = useNavigate()
  const [activeCategory, setActiveCategory] = useState<Category>('All')
  const [skills, setSkills] = useState<DisplaySkill[]>([])
  const [suggestions, setSuggestions] = useState<SkillSuggestion[]>([])
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)

  // スキルからおすすめを自動生成
  const generateSuggestions = useCallback(async (currentSkills: DisplaySkill[]) => {
    if (currentSkills.length === 0) {
      setSuggestions([])
      localStorage.removeItem(SUGGESTIONS_STORAGE_KEY)
      return
    }
    setIsLoadingSuggestions(true)
    try {
      const result = await suggestNextSkills({
        currentSkills: currentSkills.map(s => ({ name: s.name, level: s.level }))
      })
      setSuggestions(result)
      localStorage.setItem(SUGGESTIONS_STORAGE_KEY, JSON.stringify(result))
    } catch (error) {
      console.error('Failed to generate suggestions:', error)
    } finally {
      setIsLoadingSuggestions(false)
    }
  }, [])

  // プロジェクトからスキルを同期
  const syncFromProjects = useCallback(async () => {
    setIsSyncing(true)
    try {
      const syncedSkills = syncProjectsToSkills()
      const displaySkills = convertToDisplaySkills(syncedSkills)
      setSkills(displaySkills)
      // 同期後におすすめを更新
      await generateSuggestions(displaySkills)
    } finally {
      setIsSyncing(false)
    }
  }, [generateSuggestions])

  // localStorageからスキルとおすすめを読み込む + プロジェクトと同期
  useEffect(() => {
    // まずプロジェクトからスキルを同期
    syncFromProjects()

    const savedSuggestions = localStorage.getItem(SUGGESTIONS_STORAGE_KEY)
    if (savedSuggestions) {
      try {
        setSuggestions(JSON.parse(savedSuggestions))
      } catch {
        // パースに失敗した場合は空
      }
    }
  }, [syncFromProjects])

  // プロジェクト変更イベントをリッスン
  useEffect(() => {
    const unsubscribe = onProjectsUpdated(() => {
      syncFromProjects()
    })
    return unsubscribe
  }, [syncFromProjects])

  // AIスキル分析結果を保存
  const handleSaveSkills = useCallback(async (analyzedSkills: AnalyzedSkill[]) => {
    localStorage.setItem(SKILLS_STORAGE_KEY, JSON.stringify(analyzedSkills))
    const displaySkills = convertToDisplaySkills(analyzedSkills)
    setSkills(displaySkills)
    // 保存時に自動でおすすめを生成
    await generateSuggestions(displaySkills)
  }, [generateSuggestions])

  // スキルをクリックしたらProjectsページへ遷移してフィルター
  const handleSkillClick = (skillName: string) => {
    navigate(`/projects?tech=${encodeURIComponent(skillName)}`)
  }

  // スキルを削除
  const handleDeleteSkill = useCallback(async (skillName: string, e: React.MouseEvent) => {
    e.stopPropagation() // カードクリックイベントを阻止
    const currentSkills = loadSkillsFromStorage()
    const updatedSkills = currentSkills.filter(s => s.name !== skillName)
    saveSkillsToStorage(updatedSkills)
    const displaySkills = convertToDisplaySkills(updatedSkills)
    setSkills(displaySkills)
    // 削除後におすすめを更新
    await generateSuggestions(displaySkills)
  }, [generateSuggestions])

  const filteredSkills = activeCategory === 'All'
    ? skills
    : skills.filter(skill => skill.category === activeCategory)

  return (
    <div className="py-16 sm:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="mb-8 text-center text-3xl font-bold sm:text-4xl">Skills</h1>
        <p className="mx-auto mb-8 max-w-2xl text-center text-muted-foreground">
          技術スキルと習熟度を紹介します。スキルをクリックすると関連プロジェクトを表示します。
        </p>

        {/* プロジェクトから同期ボタン */}
        <div className="mx-auto mb-8 flex justify-center">
          <button
            onClick={syncFromProjects}
            disabled={isSyncing}
            className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-secondary disabled:opacity-50"
          >
            <RefreshCw className={cn("h-3 w-3", isSyncing && "animate-spin")} />
            プロジェクトから同期
          </button>
        </div>

        {/* Category Tabs - スキルがある場合のみ表示 */}
        {skills.length > 0 && (
          <div className="mx-auto mb-8 flex max-w-2xl flex-wrap justify-center gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={cn(
                  "rounded-full px-4 py-2 text-sm font-medium transition-colors",
                  activeCategory === category
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                )}
              >
                {category === 'All' ? '全て' : category}
              </button>
            ))}
          </div>
        )}

        <div className="mx-auto grid max-w-4xl gap-6 sm:grid-cols-2">
          {filteredSkills.map((skill) => {
            const IconComponent = skill.icon
            return (
              <div
                key={skill.name}
                onClick={() => handleSkillClick(skill.name)}
                className="rounded-lg border border-border bg-card p-6 text-left shadow-sm transition-all hover:shadow-md hover:border-primary/50 cursor-pointer"
              >
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <IconComponent className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold">{skill.name}</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">
                      {skill.category}
                    </span>
                    <button
                      onClick={(e) => handleDeleteSkill(skill.name, e)}
                      className="rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                      title="削除"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-500"
                    style={{ width: `${skill.level}%` }}
                  />
                </div>
                <p className="mt-2 text-right text-sm text-muted-foreground">
                  {skill.level}%
                </p>
              </div>
            )
          })}
        </div>

        {skills.length === 0 && (
          <div className="mx-auto max-w-md rounded-lg border border-dashed border-border bg-secondary/30 p-8 text-center">
            <FolderOpen className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-semibold">スキルがありません</h3>
            <p className="mb-4 text-sm text-muted-foreground">
              プロジェクトを追加すると、使用技術が自動的にスキルとして表示されます。
            </p>
            <Link
              to="/projects"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              プロジェクトを追加
            </Link>
          </div>
        )}

        {filteredSkills.length === 0 && skills.length > 0 && (
          <p className="text-center text-muted-foreground">
            該当するスキルがありません
          </p>
        )}

        {/* Next Skills Suggestions - 自動表示 */}
        {suggestions.length > 0 && (
          <div className="mx-auto mt-16 max-w-2xl">
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="mb-4 flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-semibold">次に学ぶべきスキル</h2>
                {isLoadingSuggestions && (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>
              <p className="mb-4 text-sm text-muted-foreground">
                あなたのスキルセットから、市場価値を高めるおすすめのスキルです。
              </p>
              <div className="space-y-3">
                {suggestions.map((item, index) => (
                  <div key={index} className="rounded-md bg-secondary p-3">
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        {item.skill}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{item.reason}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* AI Skill Analyzer Section */}
        <div className="mx-auto mt-16 max-w-2xl">
          <h2 className="mb-6 text-center text-2xl font-bold">
            スキルを分析・更新
          </h2>
          <AISkillAnalyzer onSave={handleSaveSkills} />
        </div>
      </div>
    </div>
  )
}
